import { Controller } from '@hotwired/stimulus';

/**
 * Lists the meters the provider keeps behind the EAN in the add-feed form, so the customer picks
 * which of them become feeds (HUB-86), and at Kenter the channels of each (HUB-83). Without a list
 * the form adds the feed as it always did.
 */
export default class extends Controller {
    static values = {
        url: String,
        texts: Object,
        // The fields holding the provider account, sent to the scan as username and password
        credentialFields: { type: Array, default: ['username', 'password'] },
        areCredentialsRequired: { type: Boolean, default: false },
        // The value of the meter kind field by the meter kind name the scan answers with
        meterKindValues: Object,
    };

    connect() {
        this.form = this.element.closest('form');
        // Keeps the margins of the content inside the block, so its height is the same while it slides
        this.element.style.display = 'flow-root';
        this.input = this.findInput('meters');
        // Only in a form where the customer picks channels too
        this.channelsInput = this.findInput('meterChannels');
        this.eanInput = this.findInput('ean');
        this.meterKindSelect = this.form.querySelector('select[name$="[meterKind]"]');
        this.credentialInputs = this.credentialFieldsValue.map((name) => this.findInput(name));
        this.scanTimeout = null;
        this.lastScanKey = null;
        this.hasAddableMeters = false;

        this.eanInput?.addEventListener('input', () => this.scheduleScan());
        // Only once a credential is typed out: every scan signs in at the provider, and a run of
        // sign-ins with half a password can get the customer's account locked
        this.credentialInputs.forEach((input) => input?.addEventListener('change', () => this.scan()));
        this.form.addEventListener('submit', (event) => this.requireSelection(event));

        this.scan();
    }

    findInput(name) {
        return this.form.querySelector(`input[name$="[${name}]"]`);
    }

    scheduleScan() {
        clearTimeout(this.scanTimeout);
        this.scanTimeout = setTimeout(() => this.scan(), 400);
    }

    async scan() {
        const ean = (this.eanInput?.value ?? '').trim();
        const credentials = this.credentialInputs.map((input) => input?.value ?? '');
        const [username = '', password = ''] = credentials;
        const scanKey = [ean, ...credentials].join('|');
        const isAccountMissing = this.areCredentialsRequiredValue && credentials.includes('');

        if (!/^\d{18}$/.test(ean) || isAccountMissing) {
            this.lastScanKey = null;
            this.showMessage('');
            return;
        }
        // Half an account of its own: wait for the other half
        if (scanKey === this.lastScanKey || (credentials.includes('') && credentials.some((value) => '' !== value))) {
            return;
        }

        this.lastScanKey = scanKey;
        this.setContent([this.createParagraph(this.textsValue.loading)]);

        try {
            const response = await fetch(this.urlValue, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ ean, username, password }),
            });
            // A newer scan took over while this one was under way
            if (scanKey !== this.lastScanKey) {
                return;
            }
            if (!response.ok) {
                await this.showFailure(response);
                return;
            }

            this.render((await response.json()).meters);
        } catch {
            this.showUnavailable();
        }
    }

    /**
     * An invalid EAN (400) needs no message here, the form reports it. Refused credentials (422 and
     * 429) come with a message of their own. Anything else is the provider being down.
     */
    async showFailure(response) {
        if (400 === response.status) {
            this.showMessage('');
        } else if ([422, 429].includes(response.status)) {
            this.showMessage((await response.json()).errors?.[0]?.error_message ?? this.textsValue.unavailable);
        } else {
            this.showUnavailable();
        }
    }

    /**
     * The input did not change, so nothing would scan again: offer to.
     */
    showUnavailable() {
        const retryButton = document.createElement('button');
        retryButton.type = 'button';
        retryButton.className = 'mb-2 cursor-pointer text-sm font-semibold text-link transition-colors hover:text-link-hover hover:underline active:opacity-70';
        retryButton.textContent = this.textsValue.retry;
        retryButton.addEventListener('click', () => {
            this.lastScanKey = null;
            this.scan();
        });

        this.showMessage(this.textsValue.unavailable, [retryButton]);
    }

    render(meters) {
        if (0 === meters.length) {
            this.showMessage(this.textsValue.not_found);
            return;
        }

        // Keep what was picked before a failed submit, else start from the first main meter
        let pickedKeys = this.input.value.split(',').filter((key) => '' !== key);
        if (0 === pickedKeys.length) {
            const mainMeter = meters.find((meter) => 'main' === meter.type && meter.hasIntervalData && !meter.isAdded);
            pickedKeys = mainMeter ? [mainMeter.key] : [];
        }
        const pickedChannels = this.readPickedChannels();
        this.selectMeterKind(meters[0].meterKind);

        const rows = meters.map((meter) => {
            const canBeAdded = meter.hasIntervalData && !meter.isAdded;

            return this.createMeterRow(
                meter,
                canBeAdded,
                canBeAdded && pickedKeys.includes(meter.key),
                pickedChannels[meter.key] ?? meter.channels,
            );
        });
        this.setContent([this.createParagraph(this.textsValue.intro), ...rows]);

        this.hasAddableMeters = meters.some((meter) => meter.hasIntervalData && !meter.isAdded);
        this.syncInput();
    }

    /**
     * The meters behind a connection are all of one kind, so the customer need not pick it
     */
    selectMeterKind(meterKind) {
        const value = this.meterKindValuesValue[meterKind];
        if (!this.meterKindSelect || undefined === value) {
            return;
        }

        this.meterKindSelect.value = String(value);
        this.meterKindSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    createMeterRow(meter, canBeAdded, isPicked, pickedChannelCodes) {
        const row = this.createCheckbox(`meter-picker-${meter.key}`, meter.key, this.describeMeter(meter), isPicked);
        const checkbox = row.querySelector('input');
        checkbox.dataset.meterKey = meter.key;
        checkbox.disabled = !canBeAdded;

        // The checkbox row is a flex row, so the channel list goes below it rather than inside it
        const meterBlock = document.createElement('div');
        meterBlock.append(row);

        if (this.channelsInput && canBeAdded && meter.selectableChannels.length > 0) {
            const channelList = this.createChannelList(meter, pickedChannelCodes);
            channelList.hidden = !isPicked;
            checkbox.addEventListener('change', () => {
                channelList.hidden = !checkbox.checked;
            });
            meterBlock.append(channelList);
        }

        return meterBlock;
    }

    createChannelList(meter, pickedChannelCodes) {
        const list = document.createElement('div');
        // Lines the channels up with the label of the meter: checkbox width plus gap
        list.className = 'mt-1 mb-2 ml-7.5';
        list.dataset.channelsOf = meter.key;

        const heading = document.createElement('div');
        heading.className = 'text-caption text-muted';
        heading.textContent = this.textsValue.channels;
        list.append(heading);

        meter.selectableChannels.forEach((channel) => {
            const description = `${channel.code} - ${channel.label} (${channel.unit})`;
            const id = `meter-picker-${meter.key}-${channel.code}`;
            list.append(this.createCheckbox(id, channel.code, description, pickedChannelCodes.includes(channel.code)));
        });

        return list;
    }

    createCheckbox(id, value, description, isChecked) {
        const row = document.createElement('div');
        row.className = 'flex min-h-9 items-center gap-2.5';

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.className = 'form-check';
        checkbox.id = id;
        checkbox.value = value;
        checkbox.checked = isChecked;
        checkbox.addEventListener('change', () => this.syncInput());

        const label = document.createElement('label');
        label.className = 'form-check-label';
        label.htmlFor = id;
        label.textContent = description;

        row.append(checkbox, label);

        return row;
    }

    readPickedChannels() {
        try {
            return JSON.parse(this.channelsInput?.value || '{}');
        } catch {
            return {};
        }
    }

    describeMeter(meter) {
        let description = meter.label;
        if (meter.description) {
            description += `: ${meter.description}`;
        }
        if (meter.meterNumber) {
            description += ` (${this.textsValue.meter_number} ${meter.meterNumber})`;
        }
        if (meter.isAdded) {
            description += ` - ${this.textsValue.already_added}`;
        } else if (!meter.hasIntervalData) {
            description += ` - ${this.textsValue.no_interval_data}`;
        }

        return description;
    }

    syncInput() {
        const pickedKeys = [...this.element.querySelectorAll('input[data-meter-key]:checked')]
            .map((checkbox) => checkbox.value);

        this.input.value = pickedKeys.join(',');
        if (this.channelsInput) {
            this.channelsInput.value = JSON.stringify(this.findPickedChannels(pickedKeys));
        }
        if (!this.findSelectionProblem()) {
            this.selectionError?.remove();
        }
    }

    /**
     * The channels checked per picked meter, for the meters offering a choice
     */
    findPickedChannels(pickedKeys) {
        const pickedChannels = {};
        this.element.querySelectorAll('[data-channels-of]').forEach((list) => {
            if (pickedKeys.includes(list.dataset.channelsOf)) {
                pickedChannels[list.dataset.channelsOf] = [...list.querySelectorAll('input:checked')]
                    .map((checkbox) => checkbox.value);
            }
        });

        return pickedChannels;
    }

    findSelectionProblem() {
        if (!this.hasAddableMeters) {
            return null;
        }
        if ('' === this.input.value) {
            return this.textsValue.none_selected;
        }
        // The channel list of a picked meter is the one shown
        const hasMeterWithoutChannels = [...this.element.querySelectorAll('[data-channels-of]')]
            .some((list) => !list.hidden && null === list.querySelector('input:checked'));

        return hasMeterWithoutChannels ? this.textsValue.no_channels_selected : null;
    }

    requireSelection(event) {
        const problem = this.findSelectionProblem();
        if (!problem) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        this.selectionError?.remove();
        this.selectionError = this.createParagraph(problem, 'text-danger');
        this.setContent([...this.element.children, this.selectionError]);
    }

    showMessage(message, extraNodes = []) {
        this.hasAddableMeters = false;
        this.input.value = '';
        if (this.channelsInput) {
            this.channelsInput.value = '';
        }
        this.setContent(message ? [this.createParagraph(message), ...extraNodes] : []);
    }

    /**
     * Swaps the content and slides the block to its new height, so the list does not pop in and
     * push the rest of the form down at once.
     */
    setContent(nodes) {
        const startHeight = this.element.offsetHeight;
        this.element.replaceChildren(...nodes);
        // The same space below as the fields of the form have, once there is anything to show
        this.element.classList.toggle('pb-5', nodes.length > 0);

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const endHeight = this.element.scrollHeight;
        this.heightAnimation?.cancel();
        this.heightAnimation = this.element.animate(
            [
                { height: `${startHeight}px`, overflow: 'hidden' },
                { height: `${endHeight}px`, overflow: 'hidden' },
            ],
            { duration: 250, easing: 'ease-out' }
        );
    }

    createParagraph(text, className = 'text-muted') {
        const paragraph = document.createElement('p');
        // The field above already keeps its space below
        paragraph.className = `mb-2 ${className}`;
        paragraph.textContent = text;

        return paragraph;
    }
}
